import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close, Warning } from '@mui/icons-material';
import { PaymentMethodsDisplay } from './PaymentMethodsDisplay';
import {
  PaymentMethodType,
  PaymentMethodsResponse,
} from '../../entities/PaymentMethods';

interface PaymentOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  paymentMethods?: PaymentMethodsResponse; // Cambiar de shortUuid a datos directos
  invoiceNumber: string;
  invoiceAmount: string;
  currency: string;
  onPayWithMercadoPago: () => void;
  onPaymentMethodSelect?: (methodType: PaymentMethodType) => void;
  highlightMethod?: PaymentMethodType;
  showOnlyHighlighted?: boolean; // Nueva prop para controlar si mostrar solo el método resaltado
}

export const PaymentOptionsDialog: React.FC<PaymentOptionsDialogProps> = ({
  open,
  onClose,
  paymentMethods,
  invoiceNumber,
  invoiceAmount,
  currency,
  onPayWithMercadoPago,
  onPaymentMethodSelect,
  highlightMethod,
  showOnlyHighlighted = false,
}) => {
  const formatAmount = (amount: string, currency: string) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const handleMethodSelect = (methodType: PaymentMethodType) => {
    if (methodType === 'mercadopago') {
      onPayWithMercadoPago();
      onClose();
    } else {
      onPaymentMethodSelect?.(methodType);
      // Para transferencia bancaria y pago presencial, mantener el diálogo abierto
      // para que el usuario pueda ver la información
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 1 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
              }}
            >
              Opciones de pago
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                wordBreak: 'break-word',
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
              }}
            >
              {invoiceNumber} - {formatAmount(invoiceAmount, currency)}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              p: 1,
              flexShrink: 0,
              mt: { xs: -0.5, sm: 0 },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        {!paymentMethods && (
          <Alert severity="warning" icon={<Warning />} sx={{ borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              No se pudieron cargar los métodos de pago
            </Typography>
          </Alert>
        )}

        {paymentMethods && (
          <PaymentMethodsDisplay
            paymentMethods={paymentMethods}
            onMethodSelect={handleMethodSelect}
            highlightMethod={highlightMethod}
            showOnlyHighlighted={showOnlyHighlighted}
          />
        )}

        {paymentMethods &&
          !paymentMethods.available_methods.mercadopago?.available &&
          !paymentMethods.available_methods.bank_transfer &&
          !paymentMethods.available_methods.cash_payment && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                No hay métodos de pago alternativos disponibles para esta
                empresa.
              </Typography>
            </Alert>
          )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cerrar
        </Button>

        {paymentMethods?.available_methods.mercadopago?.available &&
          (!highlightMethod || highlightMethod === 'mercadopago') && (
            <Button
              onClick={() => {
                onPayWithMercadoPago();
                onClose();
              }}
              variant="contained"
              sx={{ borderRadius: 2, ml: 1 }}
            >
              Pagar con MercadoPago
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentOptionsDialog;
